import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Truck, Store, UploadCloud, X, Search, User, Calendar, Plus, Trash2, Pencil, FileText, Save as SaveIcon } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { COMPANY } from "@/lib/brand";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const AdminNewOrderPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  // Customer
  const [customerType, setCustomerType] = useState("walk_in"); // walk_in | online
  const [customer, setCustomer] = useState({ name: "", company: "", address: "", phone: "", email: "", tin: "" });
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimer = useRef(null);

  // Job order — current draft service being configured
  const [category, setCategory] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [item, setItem] = useState({
    width: 4, height: 8, unit: "ft", finishing: "", quantity: 1
  });
  const [designFile, setDesignFile] = useState(null);
  // Multi-service cart
  const [services, setServices] = useState([]);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [draftId, setDraftId] = useState(null);
  const [params] = useSearchParams();
  const [designPreview, setDesignPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const designInputRef = useRef(null);

  // Payment (online only)
  const [payment, setPayment] = useState({ mode: "", amount: "", date: new Date().toISOString().slice(0, 10), proof_url: null });
  const proofInputRef = useRef(null);
  const [uploadingProof, setUploadingProof] = useState(false);

  // Fulfillment
  const [fulfillment, setFulfillment] = useState("pickup");
  const [pickupDt, setPickupDt] = useState("");
  const [googleMapLink, setGoogleMapLink] = useState("");
  const [withinMetroNaga, setWithinMetroNaga] = useState(true);
  const [distanceKm, setDistanceKm] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  // Loaders
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, svc] = await Promise.all([api.get("/settings"), api.get("/print-services?active_only=true")]);
        if (!cancelled) {
          setSettings(s.data); setServiceCatalog(svc.data);
          if (s.data.categories?.[0]?.slug) setCategory(s.data.categories[0].slug);
        }
      } catch {
        if (!cancelled) toast.error("Failed to load settings");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const categoryServices = useMemo(
    () => serviceCatalog.filter((s) => s.category === category),
    [serviceCatalog, category]
  );
  const selectedService = serviceCatalog.find((s) => s.service_id === serviceId);
  const categoryFinishings = useMemo(() => {
    if (!settings) return [];
    return settings.categories.find((c) => c.slug === category)?.finishing_options || [];
  }, [settings, category]);

  // Reset service when category changes
  useEffect(() => { setServiceId(""); setItem((p) => ({ ...p, finishing: "" })); }, [category]);

  // Customer autocomplete
  const onCustomerNameChange = (val) => {
    setCustomer({ ...customer, name: val });
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.trim().length < 2) { setCustomerSuggestions([]); setShowSuggestions(false); return; }
    searchTimer.current = setTimeout(() => {
      api.get(`/customers/search?q=${encodeURIComponent(val)}`).then(({ data }) => {
        setCustomerSuggestions(data);
        setShowSuggestions(true);
      }).catch(() => { /* silent */ });
    }, 250);
  };
  const pickCustomer = (c) => {
    setCustomer({
      name: c.name || "", company: "", address: c.address || "",
      phone: c.phone || "", email: c.email || "", tin: c.tin_number || ""
    });
    setShowSuggestions(false);
  };

  // Design file upload
  const handleDesignFile = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error("File > 100MB"); return; }
    setDesignPreview(URL.createObjectURL(file));
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDesignFile(data);
      toast.success("Design uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); setDesignPreview(null); }
  };

  // Proof upload
  const handleProofFile = async (file) => {
    if (!file) return;
    setUploadingProof(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPayment((p) => ({ ...p, proof_url: data.file_url }));
      toast.success("Proof uploaded");
    } catch (e) { toast.error("Proof upload failed"); }
    finally { setUploadingProof(false); }
  };

  // Pricing computation (VAT inclusive)
  const computation = useMemo(() => {
    if (!selectedService) return null;
    const w_ft = item.unit === "ft" ? Number(item.width) : Number(item.width) / 12;
    const h_ft = item.unit === "ft" ? Number(item.height) : Number(item.height) / 12;
    const area = +(w_ft * h_ft).toFixed(4);
    const base = Number(selectedService.base_price) || 0;
    const unit = (selectedService.unit || "").toLowerCase();
    const useArea = unit.includes("sq") || unit.includes("ft");
    const gross_per_unit = useArea ? base * area : base;
    const qty = parseInt(item.quantity) || 1;
    const line_gross = +(gross_per_unit * qty).toFixed(2);
    const net_vat = +(line_gross / 1.12).toFixed(2);
    const vat_amount = +(line_gross - net_vat).toFixed(2);
    return { area, base, useArea, gross_per_unit, qty, line_gross, net_vat, vat_amount };
  }, [selectedService, item]);

  // Shipping
  const shippingCost = useMemo(() => {
    if (!settings || fulfillment !== "delivery") return 0;
    if (withinMetroNaga) return settings.metro_naga_flat_fee;
    const extra = Math.max(0, (Number(distanceKm) || 0) - settings.free_km_threshold);
    return +(settings.metro_naga_flat_fee + extra * settings.rate_per_km_over_12).toFixed(2);
  }, [settings, fulfillment, withinMetroNaga, distanceKm]);

  const allServicesTotal = useMemo(() => services.reduce((a, s) => a + (s.computation?.line_gross || 0), 0), [services]);
  const grandTotal = allServicesTotal + shippingCost;
  const allNetVat = +(allServicesTotal / 1.12).toFixed(2);
  const allVatAmt = +(allServicesTotal - allNetVat).toFixed(2);

  // Reset current service editor
  const resetServiceEditor = () => {
    const firstCat = settings?.categories?.[0]?.slug || "tarpaulin";
    setCategory(firstCat); setServiceId(""); setItem({ width: 4, height: 8, unit: "ft", finishing: "", quantity: 1 });
    setDesignFile(null); setDesignPreview(null); setNotes(""); setEditingIdx(-1);
  };

  // Add or update service in list
  const addService = () => {
    if (!selectedService) { toast.error("Choose a service first"); return; }
    if (!computation) return;
    const entry = {
      service_id: selectedService.service_id,
      service_name: selectedService.name,
      category, width: parseFloat(item.width), height: parseFloat(item.height),
      unit: item.unit, finishing: item.finishing || "raw_cut",
      quantity: parseInt(item.quantity) || 1,
      base_price: selectedService.base_price, unit_label: selectedService.unit,
      file_url: designFile?.file_url || null, file_name: designFile?.filename || null,
      notes, computation,
    };
    if (editingIdx >= 0) {
      const next = [...services]; next[editingIdx] = entry; setServices(next);
      toast.success("Service updated");
    } else {
      setServices([...services, entry]);
      toast.success("Service added to order");
    }
    resetServiceEditor();
  };
  const editService = (idx) => {
    const s = services[idx];
    setCategory(s.category); setServiceId(s.service_id);
    setItem({ width: s.width, height: s.height, unit: s.unit, finishing: s.finishing, quantity: s.quantity });
    setDesignFile(s.file_url ? { file_url: s.file_url, filename: s.file_name } : null);
    setDesignPreview(s.file_url ? fileUrl(s.file_url) : null);
    setNotes(s.notes || ""); setEditingIdx(idx);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const removeService = (idx) => setServices(services.filter((_, i) => i !== idx));

  // Build common payload — used by submit, draft, quote
  const buildOrderPayload = (forDraft) => {
    const finalCustomerName = customer.company ? `${customer.name || customer.company}${customer.company && customer.name ? ` (${customer.company})` : ""}` : customer.name;
    const list = [...services];
    // If current editor has a valid selection but wasn't added, optionally include
    if (selectedService && computation && (forDraft || services.length === 0)) {
      list.push({
        service_id: selectedService.service_id, service_name: selectedService.name,
        category, width: parseFloat(item.width), height: parseFloat(item.height),
        unit: item.unit, finishing: item.finishing || "raw_cut",
        quantity: parseInt(item.quantity) || 1,
        base_price: selectedService.base_price, unit_label: selectedService.unit,
        file_url: designFile?.file_url || null, file_name: designFile?.filename || null,
        notes, computation,
      });
    }
    return {
      items: list.map((s) => ({
        width: s.width, height: s.height, unit: s.unit,
        finishing: s.finishing, quantity: s.quantity,
        service_id: s.service_id, service_name: s.service_name,
        category: s.category, file_url: s.file_url, notes: s.notes,
      })),
      services_full: list,
      fulfillment_type: fulfillment,
      pickup_datetime: fulfillment === "pickup" ? pickupDt : null,
      shipping: null,
      customer_name: finalCustomerName,
      customer_email: customer.email || `walkin-${Date.now()}@noemail.local`,
      customer_phone: customer.phone,
      customer_address: customer.address,
      tin_number: customer.tin,
      notes,
      source: "manual",
      walk_in: customerType === "walk_in",
      payment_details: customerType === "online" && payment.amount ? {
        mode: payment.mode, amount: parseFloat(payment.amount) || 0,
        date: payment.date, proof_url: payment.proof_url
      } : null,
      google_map_link: fulfillment === "delivery" ? googleMapLink : null,
      within_metro_naga: withinMetroNaga,
      distance_km: parseFloat(distanceKm) || 0,
    };
  };

  // Save Draft
  const saveDraft = async () => {
    if (!customer.name && !customer.company) { toast.error("Customer name/company required to save draft"); return; }
    const list = [...services];
    if (selectedService && computation) {
      list.push({
        service_id: selectedService.service_id, service_name: selectedService.name,
        category, width: parseFloat(item.width), height: parseFloat(item.height),
        unit: item.unit, finishing: item.finishing || "raw_cut",
        quantity: parseInt(item.quantity) || 1,
        base_price: selectedService.base_price, unit_label: selectedService.unit,
        file_url: designFile?.file_url || null, file_name: designFile?.filename || null,
        notes, computation,
      });
    }
    if (list.length === 0) { toast.error("Add at least one service"); return; }
    try {
      const draft = {
        draft_id: draftId, customer, customerType, services: list,
        fulfillment, pickupDt, googleMapLink, withinMetroNaga, distanceKm,
        payment, notes,
      };
      const { data } = await api.post("/drafts", draft);
      setDraftId(data.draft_id);
      toast.success("Draft saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save draft"); }
  };

  // Generate Quote PDF — via backend reportlab
  const generateQuote = async () => {
    const list = [...services];
    if (selectedService && computation) {
      list.push({
        service_name: selectedService.name, category, width: parseFloat(item.width),
        height: parseFloat(item.height), unit: item.unit,
        finishing: item.finishing, quantity: parseInt(item.quantity) || 1,
        computation,
      });
    }
    if (list.length === 0) { toast.error("Add at least one service first"); return; }
    try {
      const payload = {
        customer_name: customer.name || customer.company || "Walk-in Customer",
        customer_company: customer.company,
        customer_email: customer.email,
        customer_phone: customer.phone,
        customer_address: customer.address,
        tin_number: customer.tin,
        items: list.map((s) => ({
          service_name: s.service_name,
          width: parseFloat(s.width) || 0,
          height: parseFloat(s.height) || 0,
          unit: s.unit,
          finishing: s.finishing || "",
          quantity: parseInt(s.quantity) || 1,
          line_total: s.computation?.line_gross || 0,
        })),
        notes,
      };
      const res = await api.post("/quotes/generate", payload, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `quote-${Date.now()}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate quote");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const finalList = [...services];
    if (selectedService && computation && finalList.length === 0) {
      finalList.push({ service_id: selectedService.service_id, service_name: selectedService.name,
        category, width: item.width, height: item.height, unit: item.unit,
        finishing: item.finishing, quantity: item.quantity,
        file_url: designFile?.file_url, notes, computation });
    }
    if (finalList.length === 0) { toast.error("Add at least one service"); return; }
    // Require image for each
    const missingImg = finalList.findIndex((s) => !s.file_url);
    if (missingImg >= 0) {
      toast.error(`Upload a design file for service #${missingImg + 1} before sending to cashier`);
      return;
    }
    if (!customer.name && !customer.company) { toast.error("Customer name required"); return; }
    if (!customer.phone) { toast.error("Phone required"); return; }
    if (customerType === "online") {
      if (!customer.email) { toast.error("Email required for online customers"); return; }
      if (!payment.mode || !payment.amount) { toast.error("Payment mode and amount required"); return; }
    }
    if (fulfillment === "pickup" && !pickupDt) { toast.error("Pick a pickup date"); return; }
    if (fulfillment === "delivery") {
      if (!googleMapLink) { toast.error("Google Map link required for delivery"); return; }
      if (!withinMetroNaga && (!distanceKm || distanceKm <= 0)) { toast.error("Distance required when outside Metro Naga"); return; }
    }
    setSubmitting(true);
    try {
      const finalCustomerName = customer.company ? `${customer.name || customer.company}${customer.company && customer.name ? ` (${customer.company})` : ""}` : customer.name;
      const payload = {
        items: finalList.map((s) => ({
          width: parseFloat(s.width), height: parseFloat(s.height), unit: s.unit,
          finishing: s.finishing || "raw_cut", quantity: parseInt(s.quantity) || 1,
          service_id: s.service_id, service_name: s.service_name,
          category: s.category, file_url: s.file_url, notes: s.notes,
        })),
        fulfillment_type: fulfillment,
        pickup_datetime: fulfillment === "pickup" ? pickupDt : null,
        shipping: null,
        customer_name: finalCustomerName,
        customer_email: customer.email || `walkin-${Date.now()}@noemail.local`,
        customer_phone: customer.phone,
        customer_address: customer.address,
        tin_number: customer.tin,
        notes,
        source: "manual",
        walk_in: customerType === "walk_in",
        payment_details: customerType === "online" && payment.amount ? {
          mode: payment.mode, amount: parseFloat(payment.amount) || 0,
          date: payment.date, proof_url: payment.proof_url
        } : null,
        google_map_link: fulfillment === "delivery" ? googleMapLink : null,
        within_metro_naga: withinMetroNaga,
        distance_km: parseFloat(distanceKm) || 0,
      };
      const { data } = await api.post("/orders/create", payload);
      if (draftId) {
        try { await api.delete(`/drafts/${draftId}`); } catch { /* ignore */ }
      }
      toast.success(`Order ${data.order_id} created — sent to cashier`);
      navigate("/admin/orders");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create order");
    } finally { setSubmitting(false); }
  };

  // Load draft if ?draft=ID
  useEffect(() => {
    const id = params.get("draft");
    if (!id || !settings) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/drafts/${id}`);
        if (cancelled) return;
        setDraftId(data.draft_id);
        if (data.customer) setCustomer({ name: "", company: "", address: "", phone: "", email: "", tin: "", ...data.customer });
        if (data.customerType) setCustomerType(data.customerType);
        if (data.services) setServices(data.services);
        if (data.fulfillment) setFulfillment(data.fulfillment);
        if (data.pickupDt) setPickupDt(data.pickupDt);
        if (data.googleMapLink) setGoogleMapLink(data.googleMapLink);
        if (typeof data.withinMetroNaga === "boolean") setWithinMetroNaga(data.withinMetroNaga);
        if (data.distanceKm != null) setDistanceKm(data.distanceKm);
        if (data.payment) setPayment(data.payment);
        if (data.notes) setNotes(data.notes);
        toast.success("Draft loaded");
      } catch { toast.error("Failed to load draft"); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [settings]);

  if (loading || !settings) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] pav-fade-in" data-testid="admin-new-order-page">
      <div className="mb-6">
        <Link to="/admin/orders" className="text-sm text-neutral-500 hover:text-[#0B1E3F] inline-flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back to orders
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0B1E3F] mt-2">New Job Order</h1>
        <p className="text-neutral-600 mt-1 text-sm">New orders go to the cashier for payment confirmation, then to production.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Information */}
          <div className="pav-card" data-testid="customer-info-section">
            <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><User className="h-5 w-5" /> Customer Information</h2>
            <div className="flex gap-3 mb-4">
              <button type="button" onClick={() => setCustomerType("walk_in")}
                data-testid="customer-walkin-button"
                className={`flex-1 p-3 rounded-xl border-2 text-sm font-semibold transition ${customerType === "walk_in" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/40"}`}>
                Walk-In Customer
              </button>
              <button type="button" onClick={() => setCustomerType("online")}
                data-testid="customer-online-button"
                className={`flex-1 p-3 rounded-xl border-2 text-sm font-semibold transition ${customerType === "online" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/40"}`}>
                Online Customer
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 relative">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Full name or company</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                  <input className="pav-input !pl-10 mt-1" data-testid="customer-name-input"
                    placeholder="Type to search past customers"
                    value={customer.name}
                    onChange={(e) => onCustomerNameChange(e.target.value)}
                    onFocus={() => customerSuggestions.length > 0 && setShowSuggestions(true)} />
                </div>
                {showSuggestions && customerSuggestions.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="customer-suggestions">
                    {customerSuggestions.map((c, idx) => (
                      <button key={idx} type="button" onClick={() => pickCustomer(c)}
                        data-testid={`customer-suggestion-${idx}`}
                        className="w-full text-left p-3 hover:bg-[#F4F6FB] border-b border-[#E2E8F0] last:border-0">
                        <p className="font-semibold text-sm">{c.name}</p>
                        <p className="text-xs text-neutral-500">{c.email} · {c.phone}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Company name (optional)</label>
                <input className="pav-input mt-1" data-testid="customer-company-input"
                  value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Address</label>
                <input className="pav-input mt-1" data-testid="customer-address-input"
                  value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Contact number</label>
                <input className="pav-input mt-1" data-testid="customer-phone-input"
                  value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Email{customerType === "online" ? " *" : ""}</label>
                <input type="email" className="pav-input mt-1" data-testid="customer-email-input"
                  value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">TIN number</label>
                <input className="pav-input mt-1" data-testid="customer-tin-input"
                  value={customer.tin} onChange={(e) => setCustomer({ ...customer, tin: e.target.value })} />
              </div>
            </div>
          </div>

          {/* New Job Order */}
          <div className="pav-card" data-testid="job-order-section">
            <h2 className="font-display text-lg font-bold mb-4">New Job Order</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="pav-input mt-1" data-testid="order-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {settings.categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Service</label>
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger className="pav-input mt-1" data-testid="order-service-select"><SelectValue placeholder={categoryServices.length === 0 ? "No services in category" : "Choose service"} /></SelectTrigger>
                  <SelectContent>
                    {categoryServices.map((s) => (
                      <SelectItem key={s.service_id} value={s.service_id}>
                        {s.name} — ₱{(s.base_price || 0).toFixed(2)} {s.unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedService && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Width</label>
                    <input type="number" min="0.1" step="0.1" className="pav-input mt-1"
                      data-testid="job-width-input"
                      value={item.width} onChange={(e) => setItem({ ...item, width: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Height</label>
                    <input type="number" min="0.1" step="0.1" className="pav-input mt-1"
                      data-testid="job-height-input"
                      value={item.height} onChange={(e) => setItem({ ...item, height: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Unit</label>
                    <Select value={item.unit} onValueChange={(v) => setItem({ ...item, unit: v })}>
                      <SelectTrigger className="pav-input mt-1" data-testid="job-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ft">Feet</SelectItem>
                        <SelectItem value="in">Inches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Finishing</label>
                    <Select value={item.finishing || "_none"} onValueChange={(v) => setItem({ ...item, finishing: v === "_none" ? "" : v })}>
                      <SelectTrigger className="pav-input mt-1" data-testid="job-finishing-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {categoryFinishings.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Qty</label>
                    <input type="number" min="1" step="1" className="pav-input mt-1"
                      data-testid="job-quantity-input"
                      value={item.quantity} onChange={(e) => setItem({ ...item, quantity: e.target.value })} />
                  </div>
                </div>

                {computation && (
                  <div className="mt-4 bg-[#F4F6FB] rounded-xl p-4 text-sm space-y-1.5" data-testid="job-computation">
                    <div className="flex justify-between"><span>Area</span><span>{computation.area} sq.ft</span></div>
                    <div className="flex justify-between"><span>Base cost ({peso(computation.base)} {selectedService.unit})</span><span>{peso(computation.gross_per_unit)}</span></div>
                    <div className="flex justify-between"><span>Finishing</span><span className="capitalize">{(item.finishing || "none").replace(/_/g, " ")}</span></div>
                    <div className="flex justify-between"><span>Quantity</span><span>× {computation.qty}</span></div>
                    <div className="flex justify-between pt-2 border-t border-[#E2E8F0] font-display font-extrabold text-lg text-[#0B1E3F]">
                      <span>TOTAL</span><span data-testid="job-total">{peso(computation.line_gross)}</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* File upload */}
            <div className="mt-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-2">Design file</label>
              <div className="dropzone cursor-pointer" onClick={() => designInputRef.current?.click()} data-testid="job-file-dropzone">
                {designPreview ? (
                  <div className="flex items-center justify-center">
                    <img src={designPreview} alt="" className="max-h-44 rounded-lg" />
                  </div>
                ) : (
                  <>
                    <UploadCloud className="mx-auto h-8 w-8 text-[#0B1E3F]" />
                    <p className="text-sm mt-2 font-semibold">Click to upload reference design</p>
                    <p className="text-xs text-neutral-500">JPG · PNG · PDF · TIFF · AI · PSD · WEBP — up to 100MB</p>
                  </>
                )}
              </div>
              <input ref={designInputRef} type="file" className="hidden"
                accept=".jpg,.jpeg,.png,.pdf,.tiff,.tif,.ai,.psd,.svg,.webp"
                data-testid="job-file-input"
                onChange={(e) => handleDesignFile(e.target.files?.[0])} />
              {designFile && (
                <p className="mt-2 text-xs text-green-700">Uploaded: {designFile.filename}</p>
              )}
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Production notes (optional)</label>
              <textarea className="pav-input mt-1 min-h-[60px]" placeholder="Production notes…"
                data-testid="job-notes-input"
                value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <button type="button" onClick={addService} disabled={!selectedService}
              data-testid="add-service-button"
              className="mt-4 w-full border-2 border-dashed border-[#0B1E3F] text-[#0B1E3F] py-3 rounded-xl font-semibold hover:bg-[#F4F6FB] disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> {editingIdx >= 0 ? "Update service in order" : "Add Service to order"}
            </button>
          </div>

          {/* Payment Details (only online) */}
          {customerType === "online" && (
            <div className="pav-card" data-testid="payment-details-section">
              <h2 className="font-display text-lg font-bold mb-4">Payment Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Mode of payment</label>
                  <Select value={payment.mode} onValueChange={(v) => setPayment({ ...payment, mode: v })}>
                    <SelectTrigger className="pav-input mt-1" data-testid="payment-mode-select"><SelectValue placeholder="Choose mode" /></SelectTrigger>
                    <SelectContent>
                      {settings.payment_options.map((p) => <SelectItem key={p} value={p} className="capitalize">{p.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Amount paid (₱)</label>
                  <input type="number" min="0" step="0.01" className="pav-input mt-1"
                    data-testid="payment-amount-input"
                    value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Date of transaction</label>
                  <input type="date" className="pav-input mt-1"
                    data-testid="payment-date-input"
                    value={payment.date} onChange={(e) => setPayment({ ...payment, date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Proof / screenshot</label>
                  <div className="mt-1">
                    {payment.proof_url ? (
                      <div className="flex items-center gap-2 text-sm">
                        <img src={fileUrl(payment.proof_url)} alt="" className="h-12 w-12 rounded object-cover" />
                        <button type="button" onClick={() => setPayment({ ...payment, proof_url: null })}
                          data-testid="payment-proof-remove" className="text-red-600 hover:underline text-xs">Remove</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => proofInputRef.current?.click()}
                        data-testid="payment-proof-button"
                        className="pav-input text-sm inline-flex items-center gap-2 hover:bg-[#F4F6FB]" disabled={uploadingProof}>
                        {uploadingProof ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                        Upload screenshot
                      </button>
                    )}
                    <input ref={proofInputRef} type="file" accept="image/*" className="hidden"
                      data-testid="payment-proof-input"
                      onChange={(e) => handleProofFile(e.target.files?.[0])} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fulfillment */}
          <div className="pav-card" data-testid="fulfillment-section">
            <h2 className="font-display text-lg font-bold mb-4">Fulfillment</h2>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button type="button" onClick={() => setFulfillment("pickup")}
                data-testid="fulfillment-pickup-button"
                className={`p-4 rounded-xl border-2 text-left transition ${fulfillment === "pickup" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/40"}`}>
                <Store className="h-5 w-5 text-[#0B1E3F] mb-2" />
                <div className="font-semibold">Pickup</div>
                <div className="text-xs text-neutral-500 mt-0.5">FREE shipping</div>
              </button>
              <button type="button" onClick={() => setFulfillment("delivery")}
                data-testid="fulfillment-delivery-button"
                className={`p-4 rounded-xl border-2 text-left transition ${fulfillment === "delivery" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/40"}`}>
                <Truck className="h-5 w-5 text-[#0B1E3F] mb-2" />
                <div className="font-semibold">Delivery</div>
                <div className="text-xs text-neutral-500 mt-0.5">{peso(settings.metro_naga_flat_fee)} Metro Naga / + km</div>
              </button>
            </div>

            {fulfillment === "pickup" ? (
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Pickup date & time
                </label>
                <input type="datetime-local" className="pav-input mt-1"
                  data-testid="pickup-datetime-input"
                  value={pickupDt} onChange={(e) => setPickupDt(e.target.value)} />
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Google Map link</label>
                  <input className="pav-input mt-1" placeholder="https://maps.google.com/..."
                    data-testid="map-link-input"
                    value={googleMapLink} onChange={(e) => setGoogleMapLink(e.target.value)} />
                </div>
                <label className="flex items-center gap-2 text-sm bg-[#F4F6FB] p-3 rounded-lg" data-testid="within-metro-label">
                  <input type="checkbox" data-testid="within-metro-checkbox"
                    checked={withinMetroNaga} onChange={(e) => setWithinMetroNaga(e.target.checked)} />
                  Within Metro Naga (flat {peso(settings.metro_naga_flat_fee)})
                </label>
                {!withinMetroNaga && (
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Distance (km)</label>
                    <input type="number" min="0" step="0.1" className="pav-input mt-1"
                      data-testid="distance-input"
                      value={distanceKm} onChange={(e) => setDistanceKm(e.target.value)} />
                    <p className="text-xs text-neutral-500 mt-1">
                      First {settings.free_km_threshold}km = {peso(settings.metro_naga_flat_fee)}; +{peso(settings.rate_per_km_over_12)}/km excess
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <aside className="lg:col-span-1">
          <div className="pav-card sticky top-6" data-testid="order-summary">
            <h2 className="font-display text-lg font-bold mb-4">Order summary</h2>
            {services.length === 0 && !selectedService ? (
              <p className="text-sm text-neutral-500 py-6">Add at least one service.</p>
            ) : (
              <div className="space-y-3 text-sm">
                {services.map((s, idx) => (
                  <div key={idx} className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`summary-service-${idx}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{s.service_name}</p>
                        <p className="text-xs text-neutral-500">{s.width}×{s.height} {s.unit} · ×{s.quantity}</p>
                      </div>
                      <p className="font-display font-bold text-[#0B1E3F]">{peso(s.computation?.line_gross)}</p>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button type="button" onClick={() => editService(idx)} data-testid={`summary-edit-${idx}`}
                        className="text-xs text-[#0B1E3F] hover:underline inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                      <span className="text-neutral-300">·</span>
                      <button type="button" onClick={() => removeService(idx)} data-testid={`summary-remove-${idx}`}
                        className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                      {!s.file_url && <span className="ml-auto text-[10px] text-red-600 font-semibold uppercase">no file</span>}
                    </div>
                  </div>
                ))}
                {selectedService && (
                  <p className="text-xs text-neutral-500 italic">+ unsaved service in editor (₱{(computation?.line_gross || 0).toFixed(2)}). Click &quot;Add Service&quot; to lock it in.</p>
                )}
                <div className="pt-3 border-t border-[#E2E8F0] space-y-1">
                  <div className="flex justify-between"><span>Net VAT</span><span data-testid="summary-net-vat">{peso(allNetVat)}</span></div>
                  <div className="flex justify-between"><span>VAT (12%)</span><span data-testid="summary-vat">{peso(allVatAmt)}</span></div>
                  <div className="flex justify-between"><span>Shipping</span><span data-testid="summary-shipping">{fulfillment === "pickup" ? "FREE" : peso(shippingCost)}</span></div>
                  <div className="flex justify-between items-baseline pt-2 border-t border-[#E2E8F0]">
                    <span className="text-sm uppercase tracking-wider font-semibold">Grand total</span>
                    <span className="font-display font-extrabold text-2xl text-[#0B1E3F]" data-testid="summary-grand-total">{peso(grandTotal)}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-5 space-y-2">
              <button type="button" onClick={saveDraft}
                data-testid="save-draft-button"
                className="w-full btn-outline text-sm inline-flex items-center justify-center gap-2">
                <SaveIcon className="h-4 w-4" /> {draftId ? "Update draft" : "Save Draft"}
              </button>
              <button type="button" onClick={generateQuote}
                data-testid="generate-quote-button"
                className="w-full btn-outline text-sm inline-flex items-center justify-center gap-2">
                <FileText className="h-4 w-4" /> Generate Quote
              </button>
              <button type="submit" disabled={submitting}
                data-testid="submit-order-button"
                className="w-full btn-accent inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Send to Cashier
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-3 text-center">
              Send to Cashier requires an uploaded design file per service. Save Draft does not.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default AdminNewOrderPage;
